import type { intelligence } from '@cwos/api-client';
import { Pill } from '@cwos/ui-admin';
import { RECORD_TYPE_LABELS, recordLink } from './recordRoutes.js';

/**
 * The people, programs, sites, shifts, exceptions, automations and dashboards
 * an answer is ABOUT — as cards a person can move from into the real screen.
 *
 * This is the INVESTIGATE step of the directive's journey: an answer that names
 * a problem and then leaves the reader to go and find the affected worker by
 * hand has not finished. Each card carries the record's identity, one line of
 * context, its status words, and a link into this app's route for it.
 *
 * A UUID NEVER REACHES THE SCREEN. The payload requires a `label` and treats
 * `id` as opaque addressing — the id goes into the href and nowhere else. That
 * is a schema-level guarantee rather than a rendering habit: a record whose
 * name the caller may not see cannot be sent as a labelled record at all, so
 * there is no state in which a card falls back to printing the key.
 *
 * A LINK IS NOT A GRANT. Every destination re-checks permission at its route
 * and at its reads. See `recordRoutes.ts` for why some links say "Open in
 * Scheduling" rather than "Open this shift" — the app has the list and not the
 * record, and saying so is better than landing someone on a list they were
 * promised was a detail.
 *
 * Absent payload → renders nothing.
 */
export function RecordCards({
  records,
}: {
  records: intelligence.ResultRecords | null | undefined;
}) {
  if (records === null || records === undefined) return null;

  return (
    <section className="rn-card" aria-label={records.title}>
      <div className="rn-card-head">
        <h3 className="rn-card-title">{records.title}</h3>
      </div>
      {records.note !== undefined ? (
        <p className="rn-muted" style={{ marginTop: 0 }}>
          {records.note}
        </p>
      ) : null}
      <ul
        style={{
          display: 'grid',
          gap: 'var(--space-8)',
          listStyle: 'none',
          margin: 0,
          padding: 0,
        }}
      >
        {records.records.map((record) => (
          <li key={`${record.type}:${record.id}`}>
            <RecordCard record={record} />
          </li>
        ))}
      </ul>
    </section>
  );
}

/** One record. Exported so a single record can be rendered without a set. */
export function RecordCard({ record }: { record: intelligence.ResultRecord | null | undefined }) {
  if (record === null || record === undefined) return null;
  const link = recordLink(record);

  return (
    <article
      style={{
        border: '1px solid var(--color-line)',
        borderRadius: 'var(--radius-sm)',
        padding: 'var(--space-12)',
        background: 'var(--color-surface)',
        display: 'grid',
        gap: 'var(--space-6)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 'var(--space-12)',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            className="rn-muted"
            style={{ margin: 0, textTransform: 'uppercase', fontSize: 11, letterSpacing: '0.04em' }}
          >
            {RECORD_TYPE_LABELS[record.type]}
          </p>
          <p style={{ margin: 0, fontWeight: 600 }}>{record.label}</p>
          {record.sublabel !== undefined ? (
            <p className="rn-muted" style={{ margin: 0 }}>
              {record.sublabel}
            </p>
          ) : null}
        </div>
        {link !== null ? (
          <a href={link.href} className="rn-link" style={{ whiteSpace: 'nowrap' }}>
            {link.label}
          </a>
        ) : null}
      </div>

      {record.badges !== undefined && record.badges.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-6)' }}>
          {record.badges.map((badge) => (
            <Pill key={badge.label} variant={badge.tone}>
              {badge.label}
            </Pill>
          ))}
        </div>
      ) : null}

      {record.facts !== undefined && record.facts.length > 0 ? (
        <dl
          style={{
            display: 'grid',
            gridTemplateColumns: 'auto 1fr',
            columnGap: 'var(--space-12)',
            rowGap: 'var(--space-2)',
            margin: 0,
          }}
        >
          {record.facts.map((fact) => (
            <div key={fact.label} style={{ display: 'contents' }}>
              <dt className="rn-muted" style={{ margin: 0 }}>
                {fact.label}
              </dt>
              <dd style={{ margin: 0 }}>{fact.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </article>
  );
}
